import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';

const MEETING_LINK = 'https://meetings-eu1.hubspot.com/claus-moeldrup';

const AIDiscovery = () => {
  return (
    <div style={pageStyle}>
      <SeoHead
        title="AI Discovery | AICE"
        description="Fra AI-snak til konkrete assistenter i drift. AI Discovery giver jeres ledelse retning, prioriterede use cases og to færdigudviklede AI-assistenter."
        canonicalPath="/ydelser/ai-discovery"
      />

      {/* HERO */}
      <section style={heroSection}>
        <div style={heroInner}>
          <p style={eyebrow}>Ydelser</p>
          <h1 style={heroTitle}>Fra AI-snak til konkrete assistenter i drift</h1>
          <p style={heroText}>
            AI Discovery er et struktureret forløb der giver ledelsen retning, identificerer de bedste
            use cases og munder ud i to færdigudviklede AI-assistenter tilpasset jeres hverdag.
          </p>
          <div style={ctaRow}>
            <a href={MEETING_LINK} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <button type="button" style={primaryCta}>Snak med os</button>
            </a>
            <Link to="/ydelser" style={{ textDecoration: 'none' }}>
              <button type="button" style={secondaryCta}>Se alle ydelser</button>
            </Link>
          </div>
        </div>
      </section>

      {/* SCOPE BAR */}
      <section style={scopeBar}>
        {[
          { label: 'Varighed', value: 'Typisk 4 uger' },
          { label: 'Pris', value: 'Fra 49.000 DKK' },
          { label: 'Målgruppe', value: 'Ledelse og nøglemedarbejdere' },
          { label: 'Output', value: '2 færdige AI-assistenter' },
        ].map(({ label, value }) => (
          <div key={label} style={scopeItem}>
            <span style={scopeLabel}>{label}</span>
            <span style={scopeValue}>{value}</span>
          </div>
        ))}
      </section>

      {/* CHALLENGE */}
      <section style={section}>
        <div style={sectionHeader}>
          <p style={eyebrowSmall}>Udfordringen</p>
          <h2 style={sectionTitle}>Mange organisationer ser muligheder i AI — men mangler retning</h2>
        </div>
        <div style={twoCol}>
          <p style={bodyText}>
            Eksperimenter med ChatGPT og Copilot sker spredt og uden fælles sprog, prioritering
            eller ejerskab. Det begrænser gevinsten og skaber frustration.
          </p>
          <p style={bodyText}>
            AI Discovery giver ledelsen det fundament der mangler: en klar prioritering af de use cases
            der reelt skaber værdi, og to konkrete AI-assistenter I kan begynde at bruge med det samme.
          </p>
        </div>
      </section>

      {/* STRUCTURE */}
      <section style={{ ...section, background: '#FFF9F8', borderRadius: '24px' }}>
        <div style={sectionHeader}>
          <p style={eyebrowSmall}>Forløbet</p>
          <h2 style={sectionTitle}>Fire trin over typisk 4 uger</h2>
        </div>
        <div style={stepsGrid}>
          {[
            {
              step: '01',
              title: 'Forstå forretningen og sætte retning',
              desc: 'Foranalyse, interviews med nøglepersoner og definition af fokusområder og succeskriterier.',
            },
            {
              step: '02',
              title: 'Ledelsesworkshop og AI Discovery',
              desc: 'Kompetenceløft for ledelsen og en struktureret prioritering af de mest værdifulde AI use cases.',
            },
            {
              step: '03',
              title: 'Udvikling af to AI-assistenter',
              desc: 'Design, udvikling og test af to konkrete AI-assistenter implementeret direkte på jeres platform.',
            },
            {
              step: '04',
              title: 'Kompetenceløft og udrulning',
              desc: 'Træningsworkshop med fokus på anvendelse, gode prompts og forankring i organisationen.',
            },
          ].map(({ step, title, desc }) => (
            <div key={step} style={stepCard}>
              <span style={stepNumber}>{step}</span>
              <h3 style={stepTitle}>{title}</h3>
              <p style={stepDesc}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section style={section}>
        <div style={sectionHeader}>
          <p style={eyebrowSmall}>Udbytte</p>
          <h2 style={sectionTitle}>Hvad får I ud af det?</h2>
        </div>
        <div style={benefitsGrid}>
          {[
            { icon: '🏛️', title: 'Fælles AI-fundament i ledelsen', desc: 'Fælles sprog, forståelse og ejerskab for AI-arbejdet i organisationen.' },
            { icon: '📋', title: 'Prioriterede AI-initiativer', desc: 'Et backlog med konkrete AI-initiativer rangeret efter potentiale og gennemførlighed.' },
            { icon: '🤖', title: 'To færdige AI-assistenter', desc: 'Klar til brug fra dag ét — bygget til jeres specifikke opgaver og platform.' },
            { icon: '🎓', title: 'Kompetenceløft i hele organisationen', desc: 'Medarbejdere der ved hvordan de bruger AI effektivt og ansvarligt i hverdagen.' },
            { icon: '🗺️', title: 'Roadmap og næste skridt', desc: 'En klar plan for hvad I gør videre, og hvilke initiativer der giver mest mening at forfølge.' },
          ].map(({ icon, title, desc }) => (
            <div key={title} style={benefitCard}>
              <span style={benefitIcon}>{icon}</span>
              <h3 style={benefitTitle}>{title}</h3>
              <p style={benefitDesc}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA FOOTER */}
      <section style={ctaSection}>
        <h2 style={ctaTitle}>Klar til at finde jeres AI-potentiale?</h2>
        <p style={ctaText}>
          Book en uforpligtende samtale og hør mere om hvordan AI Discovery kan tilpasses til jeres organisation og behov.
        </p>
        <a href={MEETING_LINK} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <button type="button" style={primaryCta}>Snak med os</button>
        </a>
      </section>
    </div>
  );
};

export default AIDiscovery;

// ── Styles ──────────────────────────────────────────────

const pageStyle = {
  background: 'linear-gradient(180deg, #FFF4F0 0%, #FFFFFF 60%)',
  minHeight: '100vh',
};

const heroSection = {
  padding: '5rem clamp(1.5rem, 6vw, 6rem) 4rem',
  maxWidth: '900px',
  margin: '0 auto',
  textAlign: 'center',
};

const heroInner = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
};

const eyebrow = {
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  color: '#C03626',
  fontSize: '0.85rem',
  margin: 0,
};

const eyebrowSmall = {
  ...eyebrow,
  marginBottom: '0.4rem',
};

const heroTitle = {
  fontSize: 'clamp(2rem, 4vw, 3.2rem)',
  fontWeight: 800,
  color: '#1f1f1f',
  margin: 0,
  lineHeight: 1.15,
};

const heroText = {
  fontSize: '1.15rem',
  color: '#5C392F',
  lineHeight: 1.65,
  maxWidth: '620px',
  margin: 0,
};

const ctaRow = {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
  marginTop: '0.5rem',
};

const primaryCta = {
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  padding: '0.85rem 1.75rem',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
  boxShadow: '0 10px 24px rgba(192, 54, 38, 0.25)',
};

const secondaryCta = {
  background: '#fff',
  color: '#C03626',
  border: '1px solid #C03626',
  borderRadius: '10px',
  padding: '0.85rem 1.75rem',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
};

const scopeBar = {
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: '0',
  maxWidth: '860px',
  margin: '0 auto 4rem',
  borderRadius: '16px',
  border: '1px solid #F1D5CF',
  background: '#fff',
  boxShadow: '0 8px 24px rgba(192,54,38,0.07)',
  overflow: 'hidden',
};

const scopeItem = {
  flex: '1 1 160px',
  padding: '1.25rem 1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  borderRight: '1px solid #F1D5CF',
};

const scopeLabel = {
  fontSize: '0.78rem',
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  color: '#C03626',
  fontWeight: 700,
};

const scopeValue = {
  fontSize: '1.05rem',
  fontWeight: 600,
  color: '#1f1f1f',
};

const section = {
  maxWidth: '1000px',
  margin: '0 auto',
  padding: '3rem clamp(1rem, 4vw, 3rem)',
};

const sectionHeader = {
  marginBottom: '2rem',
};

const sectionTitle = {
  fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
  fontWeight: 700,
  color: '#1f1f1f',
  margin: 0,
};

const twoCol = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '1.5rem',
};

const bodyText = {
  fontSize: '1.05rem',
  lineHeight: 1.7,
  color: '#5C392F',
  margin: 0,
};

const stepsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1.5rem',
};

const stepCard = {
  background: '#fff',
  borderRadius: '16px',
  padding: '1.5rem',
  border: '1px solid #F1D5CF',
  boxShadow: '0 8px 20px rgba(192,54,38,0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const stepNumber = {
  fontSize: '1.8rem',
  fontWeight: 800,
  color: '#FFD6CC',
  lineHeight: 1,
};

const stepTitle = {
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#1f1f1f',
  margin: 0,
};

const stepDesc = {
  fontSize: '0.97rem',
  color: '#5C392F',
  lineHeight: 1.6,
  margin: 0,
};

const benefitsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1.5rem',
};

const benefitCard = {
  background: '#fff',
  borderRadius: '16px',
  padding: '1.5rem',
  border: '1px solid #F1D5CF',
  boxShadow: '0 8px 20px rgba(192,54,38,0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const benefitIcon = {
  fontSize: '1.75rem',
};

const benefitTitle = {
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#1f1f1f',
  margin: 0,
};

const benefitDesc = {
  fontSize: '0.97rem',
  color: '#5C392F',
  lineHeight: 1.6,
  margin: 0,
};

const ctaSection = {
  background: 'linear-gradient(135deg, #FFF4F0, #FFEAE4)',
  borderTop: '1px solid #F1D5CF',
  padding: '4rem clamp(1.5rem, 6vw, 6rem)',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
};

const ctaTitle = {
  fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
  fontWeight: 700,
  color: '#1f1f1f',
  margin: 0,
};

const ctaText = {
  fontSize: '1.05rem',
  color: '#5C392F',
  maxWidth: '500px',
  lineHeight: 1.65,
  margin: 0,
};
